import { useCallback, useMemo, useRef, useState } from 'react';
import {
  FieldError,
  FieldErrors,
  FieldValues,
  Path,
  UseFormSetValue,
  useForm,
  useFormState,
} from 'react-hook-form';
import { Channel, useChannelContext } from 'scbk-services';
import { FieldEl, SubmitFn, UsePageFormOptions, UsePageFormReturn } from '../type';
import { createUseField, createUseFields } from './useField';

type AnyObject = Record<string, any>;

type FocusOptions = {
  shouldScroll?: boolean;
  shouldSelect?: boolean;
};

type FlattenOptions = {
  expandArray?: boolean;
  maxArrayLength?: number;
  leafOnly?: boolean;
};

const normalizePath = (p?: string) => p?.replace(/\[(\d+)\]/g, '.$1');

const isSameOrParentPath = (active?: string, name?: string) => {
  if (!active || !name) {
    return false;
  }

  const a = normalizePath(active);
  const n = normalizePath(name);
  return a === n || n?.startsWith(`${a}.`);
};

/**
 *
 * @param value
 * @param options
 * @returns
 */
function flattenDefaultValuePaths(value: unknown, options: FlattenOptions = {}): string[] {
  const { expandArray = true, maxArrayLength = 200, leafOnly = true } = options;

  const result: string[] = [];

  const walk = (v: any, prefix: string) => {
    if (v == null) {
      if (prefix && !leafOnly) {
        result.push(prefix);
      }
      return;
    }

    const t = typeof v;
    if (t !== 'object') {
      if (prefix) {
        result.push(prefix);
      }
      return;
    }

    if (Array.isArray(v)) {
      if (!expandArray) {
        if (prefix) {
          result.push(prefix);
        }
        return;
      }

      const len = Math.min(v.length, maxArrayLength);
      for (let i = 0; i < len; i++) {
        const p = prefix ? `${prefix}.${i}` : String(i);
        walk(v[i], p);
      }
      return;
    }

    const obj = v as AnyObject;
    const keys = Object.keys(obj);
    if (!keys.length) {
      if (prefix) {
        result.push(prefix);
      }
      return;
    }

    for (const k of keys) {
      const p = prefix ? `${prefix}.${k}` : k;
      walk(obj[k], p);
    }

    if (!leafOnly && prefix) {
      result.push(prefix);
    }
  };
  walk(value, '');
  return result;
}

/**
 * 설정된 룰을 기반으로 필드 order를 확정 후 map에 저장한다.
 * @param paths
 * @returns
 */
function createRuleOrderMap(paths: string[]) {
  const map = new Map<string, number>();
  let order = 1;
  for (const p of paths) {
    if (!map.has(p)) {
      map.set(p, order++);
    }
  }
  return map;
}

/**
 *
 * @param obj
 * @param path
 * @returns
 */
function getByPath(obj: unknown, path: string): unknown {
  if (!obj || typeof obj !== 'object') return;

  const normalized = path.replace(/\[(\d+)\]/g, (_, index) => `${index}`);
  const keys = normalized.split('.').filter(Boolean);

  let cur: any = obj;
  for (const k of keys) {
    if (cur == null) return;
    cur = cur[k];
  }
  return cur;
}

/**
 *
 * @param value
 */
function pickFieldError(value: unknown): FieldError | undefined {
  if (!value || typeof value !== 'object') {
    return;
  }

  const v = value as AnyObject;

  if (typeof v.message === 'string' || v.type === 'string') {
    return v as FieldError;
  }

  return;
}

/**
 *
 * @param errors
 * @param prefix
 * @returns
 */
function flattenErrorPaths(errors: any, prefix = ''): string[] {
  if (!errors) {
    return [];
  }

  if (typeof errors.message === 'string' || typeof errors.type === 'string') {
    return prefix ? [prefix] : [];
  }

  return Object.keys(errors).flatMap((k) => {
    const p = prefix ? `${prefix}.${k}` : k;
    return flattenErrorPaths(errors[k], p);
  });
}

/**
 *
 * @param errors
 * @param getRuleOrder
 * @returns
 */
function findErrorByRuleOrder<T extends FieldValues>(
  errors: any,
  getRuleOrder: (name: Path<T>) => number,
) {
  const paths = flattenErrorPaths(errors).filter(Boolean);
  paths.sort((a, b) => getRuleOrder(a as any) - getRuleOrder(b as any));
  return paths[0] ? normalizePath(paths[0]) : null;
}

/**
 *
 * @param options
 * @returns
 */
function usePageForm<T extends FieldValues>(options: UsePageFormOptions<T>): UsePageFormReturn<T> {
  const [activeErrorField, setActiveErrorField] = useState<string>();
  const { channel } = useChannelContext();
  const {
    defaultValues,
    shouldFocusError = true,
    scrollToError = true,
    onSubmitError,
    delayFocusTime = 0,
    ...rest
  } = options || {};

  const form = useForm<T>({
    mode: rest.mode ?? 'onSubmit',
    reValidateMode: rest.reValidateMode ?? 'onChange',
    shouldFocusError,
    defaultValues,

    ...rest,
  });

  const {
    setError,
    // formState,
    setFocus,
    handleSubmit,
  } = form;

  const { errors } = useFormState({ control: form.control });
  //   const activeValue = useWatch({ control: form.control });

  /**
   *
   */
  const refMapRef = useRef(new Map<string, FieldEl>());

  /**
   *
   */
  const focusRequestMap = new Map<string, () => void>();

  /**
   * 필드 path 별 ref 를 획득한다.
   * @param name
   * @returns
   */
  const getFieldRef = (name: Path<T>) => {
    return refMapRef.current.get(String(name));
  };

  /**
   * 필드별 ref 정보를 저장한다.
   * @param name
   * @param el
   */
  const setFieldRef = (name: Path<T>, el: FieldEl) => {
    refMapRef.current.set(String(name), el);
  };

  /**
   * form에 설정된 룰을 기반으로 필드 order를 생성한다.
   * focus 이동시 생성된 필드 order를 기반으로 처리한다.
   */
  const ruleOrder = useMemo(() => {
    const dv = defaultValues ?? {};
    const paths = flattenDefaultValuePaths(dv);
    return createRuleOrderMap(paths);
  }, [defaultValues]);

  /**
   * 필드 order를 획득한다.
   * @param name 필드명
   * @returns
   */
  const getRuleOrder = (name: Path<T>) => {
    return ruleOrder.get(name) ?? 9999;
  };
  /**
   * 지정한 필드로 스크롤 이동한다.
   * @param field 필드명
   * @returns
   */
  const scrollToField = (field: string) => {
    const el = getFieldRef(field as Path<T>);
    if (!el) {
      return false;
    }

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return true;
  };

  /**
   * 폼 입력요소 focus 처리 및 scrollIntoView 처리
   */
  const setFocusWithScroll = useCallback(
    (path: string, options: FocusOptions = {}) => {
      // 접근성 대응
      // 인뱅인경우 focus 무력화
      if (channel === Channel.IB) {
        return;
      }

      const { shouldScroll = true, shouldSelect = false } = options;

      if (shouldScroll) {
        scrollToField(path);
      }

      let focused = false;
      try {
        form.setFocus(path as any, { shouldSelect });
        focused = true;
      } catch {
        focused = false;
      }

      runFocusRequest(path);
      return focused;
    },
    [channel],
  );

  /**
   * 폼 입력요소 focus 처리 및 scrollIntoView 처리
   */
  const setErrorFocusWithScroll = useCallback((path: string, options: FocusOptions = {}) => {
    const { shouldScroll = true, shouldSelect = false } = options;

    if (shouldScroll) {
      scrollToField(path);
    }

    let focused = false;
    try {
      form.setFocus(path as any, { shouldSelect });
      focused = true;
    } catch {
      focused = false;
    }

    runFocusRequest(path);
    return focused;
  }, []);

  /**
   * 입력요소가 아닌 div 필드 요소 focus 처리를 위해 focus 처리 callback 함수를 등록한다.
   * @param name 필드명
   * @param fn focus 처리 콜백 함수
   */
  const registerFocusRequest = (name: string, fn?: () => void) => {
    if (!fn) {
      focusRequestMap.delete(name);
    } else {
      focusRequestMap.set(name, fn);
    }
  };

  /**
   * 필드명으로 등록된 focus 처리 콜백함수를 실행한다.
   * @param name 필드명
   * @returns
   */
  const runFocusRequest = (name: string) => {
    const fn = focusRequestMap.get(name);
    if (!fn) {
      return;
    }

    fn();
  };

  /**
   * 필드별 에러 메시지를 설정한다.
   */
  const setFieldError = useCallback(
    (field: Path<T>, message: string) => {
      //필드별 에러 등록
      setError(field, { type: 'manual', message });
      setActiveErrorField(field); // 에러 순서 상관없이 error active 시켜주기 위해서

      //에러 필드 위치로 스크롤 이동
      if (scrollToError) {
        scrollToField(field);
      }

      //자동 focus 처리
      if (shouldFocusError) {
        setFocus(field);
      }
    },
    [setError, scrollToError, scrollToField, shouldFocusError, setFocus],
  );

  /**
   *
   */
  const setErrors = useCallback(
    (errors: Partial<Record<Path<T>, string>>) => {
      //
      const fields = Object.keys(errors) as Path<T>[];
      if (fields.length === 0) {
        return;
      }

      fields.forEach((f) => {
        const msg = errors[f];
        if (!msg) {
          return;
        }

        setError(f, { type: 'custom', message: msg });
      });

      const first = fields[0];
      if (first) {
        if (scrollToError) {
          scrollToField(first);
        }

        if (shouldFocusError) {
          setFocus(first);
        }
      }
    },
    [setError, scrollToError, scrollToField, shouldFocusError, setFocus],
  );

  /**
   *
   * @param name
   * @param value
   * @param option
   */
  const setSafeValue: UseFormSetValue<T> = (name, value, option) => {
    form.setValue(name, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
      ...option,
    });
  };

  /**
   * 필드 에러여부를 조회한다.
   */
  const isError = useCallback(
    (field: Path<T>): boolean => {
      if (!isSameOrParentPath(activeErrorField, String(field))) {
        return false;
      }

      const raw = getByPath(errors, String(field));
      return !!pickFieldError(raw);
    },
    [errors, activeErrorField],
  );

  /**
   * 필드 에러메시지를 가져온다.
   */
  const getFieldError = useCallback(
    (field: Path<T>): string | undefined => {
      if (!isSameOrParentPath(activeErrorField, String(field))) {
        return;
      }

      const raw = getByPath(errors, String(field));
      const error = pickFieldError(raw);
      if (!error) {
        return;
      }

      return typeof error.message === 'string' ? error.message : undefined;
    },
    [errors, activeErrorField],
  );

  /**
   *
   * @param current
   */
  const focusNextField = (current: Path<T>) => {
    const curOrder = getRuleOrder(current);
    const entries = Array.from(ruleOrder.entries()).sort((a, b) => a[1] - b[1]);
    const next = entries.find(([, o]) => o > curOrder)?.[0];
    if (!next) {
      return;
    }

    setFocusWithScroll(next);
  };

  /**
   * 폼 submit
   * @param fn submit 실행 함수
   * @returns
   */
  const submit = (fn: SubmitFn<any>) => async (e?: React.BaseSyntheticEvent) => {
    return handleSubmit(
      async (data) => {
        console.log('# handleSubmit onValid data', data);
        try {
          //활성화된 에러필드 정보 초기화 처리
          setActiveErrorField('');

          return await fn(data);
        } catch (err) {
          console.error('[PageForm] submit error:', err);
          if (onSubmitError) {
            onSubmitError(err);
          }
        }
      },
      async (errors: FieldErrors<T>) => {
        console.log('# onInvalid errors', errors, ', ruleOrder', ruleOrder);
        const first = findErrorByRuleOrder(errors, getRuleOrder);
        if (!first) {
          return;
        }
        console.log('# onInvalid first', first, errors[first]?.message);

        if (errors[first]?.message === 'false') {
          return;
        }

        setActiveErrorField(normalizePath(String(first)));
        if (shouldFocusError) {
          setTimeout(() => {
            //setFocusWithScroll(String(first));
            setErrorFocusWithScroll(String(first));
          }, delayFocusTime);
        }
      },
    )(e);
  };

  /**
   *
   */
  const useField = useMemo(
    () =>
      createUseField(
        {
          control: form.control,
          trigger: form.trigger,
          watch: form.watch,
          getValues: form.getValues,
          activeErrorField,
          setActiveErrorField,
          focusNextField,
          formState: form.formState,
          clearErrors: form.clearErrors,
        },
        setFieldRef,
      ),
    [form.control, form.trigger, form.getValues, form.watch, form.getValues, activeErrorField],
  );

  /**
   *
   */
  const useFields = useMemo(() => createUseFields(useField), [useField]);

  /**
   * onSubmit 이외에 검증이 오류가 났을때 실시간으로 ui 반영 가능하도록
   */
  //   useEffect(() => {
  //     if (rest?.mode !== 'onSubmit' && !_.isEmpty(errors)) {
  //       debugger;
  //       const first = findErrorByRuleOrder(errors, getRuleOrder);
  //       if (!first) {
  //         return;
  //       }

  //       if (errors[first]?.message === 'false') {
  //         return;
  //       }

  //       setActiveErrorField(normalizePath(String(first)));
  //     }
  //   }, [errors, activeValue, rest?.mode, activeErrorField]);

  return {
    ...form,
    useField,
    useFields,
    setValue: setSafeValue,
    scrollToField,
    setFocus: setFocusWithScroll,
    isError,
    setFieldError,
    getFieldError,
    setErrors,
    //focusFirstError,
    registerFocusRequest,
    submit,
    activeErrorField,
    setActiveErrorField,
    focusNextField,
  };
}

export { usePageForm };
